import { useRef, useState, useEffect, useCallback } from "react";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SOURCE_CHAINS = ["BSC", "ETH", "XRP", "SOL", "RH", "BESC"];
const ISSUE_TYPES = [
  { value: "stuck_transaction", label: "Stuck / Pending Transaction" },
  { value: "wrong_amount",      label: "Wrong Amount Received" },
  { value: "lost_funds",        label: "Lost Funds" },
  { value: "bridge_error",      label: "Bridge Error / Failed TX" },
  { value: "account_issue",     label: "Account / Wallet Issue" },
  { value: "other",             label: "Other" },
];
const ISSUE_LABEL = Object.fromEntries(ISSUE_TYPES.map(t => [t.value, t.label]));

/* ─── helpers ─────────────────────────────────────────────────────────────── */

function getStoredAuth() {
  try { return JSON.parse(localStorage.getItem("sp_auth") || "null"); } catch { return null; }
}
function setStoredAuth(v) {
  try { if (v) localStorage.setItem("sp_auth", JSON.stringify(v)); else localStorage.removeItem("sp_auth"); } catch {}
}
function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/* ─── root ────────────────────────────────────────────────────────────────── */

export default function BridgeSupportPage() {
  const [auth, setAuth]         = useState(getStoredAuth);
  const [view, setView]         = useState("reports"); // reports | new
  const [authView, setAuthView] = useState("login");   // login | register | forgot | sent | reset
  const [resetToken, setResetToken] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const rt = p.get("reset");
    if (rt) { setResetToken(rt); setAuthView("reset"); }
  }, []);

  const login  = d => { setAuth(d); setStoredAuth(d); setView("reports"); };
  const logout = () => { setAuth(null); setStoredAuth(null); setAuthView("login"); };

  return (
    <div className="sp-page">
      <Styles />
      <div className="sp-shell">

        {/* ── header ── */}
        <header className="sp-header">
          <div className="sp-header-brand">
            <span className="sp-header-icon">⚡</span>
            <span className="sp-header-title">BESC Bridge Support</span>
          </div>
          {auth && (
            <div className="sp-header-user">
              <span className="sp-header-email">{auth.user.email}</span>
              <button className="sp-link-btn" onClick={logout}>Sign out</button>
            </div>
          )}
        </header>

        {/* ── body ── */}
        {!auth ? (
          <AuthPanel
            view={authView}
            onSwitch={setAuthView}
            onLogin={login}
            resetToken={resetToken}
            onResetDone={() => { setResetToken(null); setAuthView("login"); window.history.replaceState({}, "", "/support"); }}
          />
        ) : (
          <Dashboard auth={auth} view={view} onViewChange={setView} />
        )}

      </div>
    </div>
  );
}

/* ─── auth panel ──────────────────────────────────────────────────────────── */

function AuthPanel({ view, onSwitch, onLogin, resetToken, onResetDone }) {
  return (
    <div className="sp-auth-wrap">
      <div className="sp-auth-card">
        {view === "login"    && <LoginForm    onLogin={onLogin} onSwitch={onSwitch} />}
        {view === "register" && <RegisterForm onLogin={onLogin} onSwitch={onSwitch} />}
        {view === "forgot"   && <ForgotForm   onSwitch={onSwitch} />}
        {view === "sent"     && <ResetSent    onSwitch={onSwitch} />}
        {view === "reset"    && <ResetForm    token={resetToken} onDone={onResetDone} />}
      </div>
      <p className="sp-auth-note">
        Registered users can submit tickets, track status, and receive email notifications when resolved.
      </p>
    </div>
  );
}

function LoginForm({ onLogin, onSwitch }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr]           = useState("");
  const [busy, setBusy]         = useState(false);

  const submit = async e => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/support/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Login failed");
      onLogin(j);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  return (
    <>
      <div className="sp-form-head">
        <h2 className="sp-form-title">Welcome back</h2>
        <p className="sp-form-sub">Sign in to view and manage your support tickets</p>
      </div>
      {err && <Err msg={err} onClose={() => setErr("")} />}
      <form onSubmit={submit} className="sp-form" noValidate>
        <Field label="Email" required>
          <input className="sp-input" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" required />
        </Field>
        <Field label="Password" required>
          <input className="sp-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
        </Field>
        <Btn busy={busy} label="Sign In" />
      </form>
      <div className="sp-form-footer">
        <button className="sp-link-btn" onClick={() => onSwitch("forgot")}>Forgot password?</button>
        <span className="sp-sep">·</span>
        <button className="sp-link-btn" onClick={() => onSwitch("register")}>Create an account</button>
      </div>
    </>
  );
}

function RegisterForm({ onLogin, onSwitch }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [err, setErr]           = useState("");
  const [busy, setBusy]         = useState(false);

  const submit = async e => {
    e.preventDefault(); setErr("");
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    if (password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/support/auth/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Registration failed");
      onLogin(j);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  return (
    <>
      <div className="sp-form-head">
        <h2 className="sp-form-title">Create account</h2>
        <p className="sp-form-sub">Register to submit tickets and track their status</p>
      </div>
      {err && <Err msg={err} onClose={() => setErr("")} />}
      <form onSubmit={submit} className="sp-form" noValidate>
        <Field label="Email" required>
          <input className="sp-input" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" required />
        </Field>
        <Field label="Password" required>
          <input className="sp-input" type="password" placeholder="Minimum 8 characters" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" required />
        </Field>
        <Field label="Confirm password" required>
          <input className="sp-input" type="password" placeholder="Repeat your password" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" required />
        </Field>
        <Btn busy={busy} label="Create Account" />
      </form>
      <div className="sp-form-footer">
        <span className="sp-form-sub" style={{ fontSize: 13 }}>Already have an account?</span>
        <button className="sp-link-btn" onClick={() => onSwitch("login")}>Sign in</button>
      </div>
    </>
  );
}

function ForgotForm({ onSwitch }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy]   = useState(false);

  const submit = async e => {
    e.preventDefault(); setBusy(true);
    try { await fetch(`${BACKEND}/support/auth/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) }); } catch {}
    onSwitch("sent");
  };

  return (
    <>
      <div className="sp-form-head">
        <h2 className="sp-form-title">Reset password</h2>
        <p className="sp-form-sub">Enter your email and we'll send you a reset link</p>
      </div>
      <form onSubmit={submit} className="sp-form" noValidate>
        <Field label="Email" required>
          <input className="sp-input" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required />
        </Field>
        <Btn busy={busy} label="Send Reset Link" />
      </form>
      <div className="sp-form-footer">
        <button className="sp-link-btn" onClick={() => onSwitch("login")}>← Back to sign in</button>
      </div>
    </>
  );
}

function ResetSent({ onSwitch }) {
  return (
    <>
      <div className="sp-success-state">
        <div className="sp-success-icon">✉</div>
        <h2 className="sp-form-title">Check your inbox</h2>
        <p className="sp-form-sub">If an account exists for that email, we've sent a reset link. It expires in 60 minutes.</p>
      </div>
      <div className="sp-form-footer" style={{ marginTop: 24 }}>
        <button className="sp-link-btn" onClick={() => onSwitch("login")}>← Back to sign in</button>
      </div>
    </>
  );
}

function ResetForm({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [err, setErr]           = useState("");
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(false);

  const submit = async e => {
    e.preventDefault(); setErr("");
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    if (password.length < 8)  { setErr("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/support/auth/reset-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token, password }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Reset failed");
      setDone(true);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  if (done) return (
    <div className="sp-success-state">
      <div className="sp-success-icon" style={{ color: "#4ecb71" }}>✓</div>
      <h2 className="sp-form-title">Password updated</h2>
      <p className="sp-form-sub">You can now sign in with your new password.</p>
      <button className="sp-btn sp-btn--primary" style={{ marginTop: 20 }} onClick={onDone}>Sign In</button>
    </div>
  );

  return (
    <>
      <div className="sp-form-head">
        <h2 className="sp-form-title">Set new password</h2>
      </div>
      {err && <Err msg={err} onClose={() => setErr("")} />}
      <form onSubmit={submit} className="sp-form" noValidate>
        <Field label="New password" required>
          <input className="sp-input" type="password" placeholder="Minimum 8 characters" value={password} onChange={e => setPassword(e.target.value)} required />
        </Field>
        <Field label="Confirm password" required>
          <input className="sp-input" type="password" placeholder="Repeat your password" value={confirm} onChange={e => setConfirm(e.target.value)} required />
        </Field>
        <Btn busy={busy} label="Set Password" />
      </form>
    </>
  );
}

/* ─── dashboard (logged in) ───────────────────────────────────────────────── */

function Dashboard({ auth, view, onViewChange }) {
  return (
    <div className="sp-dashboard">
      <nav className="sp-nav">
        <button className={`sp-nav-item ${view === "reports" ? "sp-nav-item--active" : ""}`} onClick={() => onViewChange("reports")}>
          <span className="sp-nav-icon">📋</span> My Reports
        </button>
        <button className={`sp-nav-item ${view === "new" ? "sp-nav-item--active" : ""}`} onClick={() => onViewChange("new")}>
          <span className="sp-nav-icon">＋</span> New Report
        </button>
      </nav>
      <div className="sp-dashboard-body">
        {view === "reports" && <MyReports auth={auth} onNew={() => onViewChange("new")} />}
        {view === "new"     && <TicketForm auth={auth} onSubmitted={() => onViewChange("reports")} />}
      </div>
    </div>
  );
}

/* ─── my reports ──────────────────────────────────────────────────────────── */

function MyReports({ auth, onNew }) {
  const [tickets, setTickets] = useState(null);
  const [err, setErr]         = useState("");
  const [open, setOpen]       = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await fetch(`${BACKEND}/support/auth/my-tickets`, { headers: { Authorization: `Bearer ${auth.token}` } });
      if (r.status === 401) { setErr("Session expired — please sign in again."); return; }
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Failed to load tickets");
      setTickets(j);
    } catch (ex) { setErr(ex.message); }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);

  if (err) return (
    <div className="sp-center-state">
      <p className="sp-state-msg sp-state-msg--error">{err}</p>
      <button className="sp-btn sp-btn--ghost" onClick={load} style={{ marginTop: 12 }}>Try again</button>
    </div>
  );

  if (tickets === null) return (
    <div className="sp-center-state">
      <div className="sp-spinner" />
      <p className="sp-state-msg">Loading your reports…</p>
    </div>
  );

  if (tickets.length === 0) return (
    <div className="sp-center-state">
      <div className="sp-empty-graphic">
        <div className="sp-empty-circle" />
        <span className="sp-empty-emoji">📭</span>
      </div>
      <h3 className="sp-state-title">No reports yet</h3>
      <p className="sp-state-msg">When you submit a support request it will appear here.</p>
      <button className="sp-btn sp-btn--primary" style={{ marginTop: 20 }} onClick={onNew}>Submit a Report</button>
    </div>
  );

  const openCount     = tickets.filter(t => t.status === "open").length;
  const resolvedCount = tickets.filter(t => t.status === "resolved").length;

  return (
    <div className="sp-reports">
      <div className="sp-reports-topbar">
        <div className="sp-reports-stats">
          <div className="sp-stat">
            <span className="sp-stat-num">{tickets.length}</span>
            <span className="sp-stat-label">Total</span>
          </div>
          <div className="sp-stat-divider" />
          <div className="sp-stat">
            <span className="sp-stat-num" style={{ color: "#d4a843" }}>{openCount}</span>
            <span className="sp-stat-label">Under Review</span>
          </div>
          <div className="sp-stat-divider" />
          <div className="sp-stat">
            <span className="sp-stat-num" style={{ color: "#4ecb71" }}>{resolvedCount}</span>
            <span className="sp-stat-label">Resolved</span>
          </div>
        </div>
        <button className="sp-icon-btn" title="Refresh" onClick={load}>↻</button>
      </div>

      <div className="sp-ticket-list">
        {tickets.map(t => (
          <TicketRow key={t.id} ticket={t} expanded={open === t.id} onToggle={() => setOpen(open === t.id ? null : t.id)} />
        ))}
      </div>
    </div>
  );
}

function TicketRow({ ticket: t, expanded, onToggle }) {
  const isResolved = t.status === "resolved";
  return (
    <div className={`sp-ticket ${expanded ? "sp-ticket--open" : ""} ${isResolved ? "sp-ticket--resolved" : ""}`}>
      <button className="sp-ticket-header" onClick={onToggle}>
        <div className="sp-ticket-meta">
          <span className={`sp-badge ${isResolved ? "sp-badge--green" : "sp-badge--amber"}`}>
            {isResolved ? "Resolved" : "Under Review"}
          </span>
          <span className="sp-ticket-type">{ISSUE_LABEL[t.issue_type] || t.issue_type}</span>
        </div>
        <div className="sp-ticket-aside">
          <span className="sp-ticket-date">{fmtDate(t.created_at)}</span>
          <span className="sp-ticket-id">#{t.id}</span>
          <span className="sp-chevron">{expanded ? "▲" : "▼"}</span>
        </div>
      </button>

      {expanded && (
        <div className="sp-ticket-body">
          <div className="sp-ticket-message">
            <span className="sp-field-label">Your report</span>
            <p className="sp-field-text">{t.message}</p>
          </div>

          {(t.wallet_address || t.source_chain || t.tx_hash) && (
            <div className="sp-ticket-fields">
              {t.wallet_address && (
                <div className="sp-field-row">
                  <span className="sp-field-label">Wallet</span>
                  <span className="sp-field-val sp-mono">{t.wallet_address}</span>
                </div>
              )}
              {t.source_chain && (
                <div className="sp-field-row">
                  <span className="sp-field-label">Chain</span>
                  <span className="sp-field-val">{t.source_chain}</span>
                </div>
              )}
              {t.tx_hash && (
                <div className="sp-field-row">
                  <span className="sp-field-label">TX hash</span>
                  <span className="sp-field-val sp-mono sp-hash">{t.tx_hash}</span>
                </div>
              )}
            </div>
          )}

          {isResolved ? (
            <div className="sp-resolution">
              <div className="sp-resolution-head">
                <span className="sp-resolution-dot" />
                <span className="sp-resolution-label">Resolution</span>
                {t.resolved_at && <span className="sp-resolution-date">{fmtDate(t.resolved_at)}</span>}
              </div>
              <p className="sp-resolution-text">{t.admin_notes || "Your ticket has been resolved by our support team."}</p>
            </div>
          ) : (
            <div className="sp-pending">
              <span className="sp-pending-dot" />
              <span>Under review — you'll receive an email when this is resolved</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── new ticket form ─────────────────────────────────────────────────────── */

function TicketForm({ auth, onSubmitted }) {
  const [form, setForm]         = useState({ name: "", walletAddress: "", sourceChain: "", txHash: "", issueType: "", message: "" });
  const [screenshot, setShot]   = useState(null);
  const [preview, setPreview]   = useState(null);
  const [err, setErr]           = useState("");
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(null);
  const fileRef                  = useRef(null);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleFile = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Only image files accepted."); return; }
    if (f.size > 5 * 1024 * 1024)    { setErr("Screenshot must be under 5 MB."); return; }
    setShot(f); setPreview(URL.createObjectURL(f)); setErr("");
  };
  const removeFile = () => {
    setShot(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async e => {
    e.preventDefault(); setErr("");
    if (!form.name.trim())    { setErr("Name is required."); return; }
    if (!form.message.trim()) { setErr("Please describe your issue."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (screenshot) fd.append("screenshot", screenshot);
      const r = await fetch(`${BACKEND}/support`, { method: "POST", headers: { Authorization: `Bearer ${auth.token}` }, body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Submission failed");
      setDone(j.ticketId);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  if (done) return (
    <div className="sp-center-state">
      <div className="sp-success-icon" style={{ color: "#4ecb71", fontSize: 48, marginBottom: 12 }}>✓</div>
      <h3 className="sp-state-title">Report #{done} submitted</h3>
      <p className="sp-state-msg">Our team will review it and you'll receive an email when it's resolved. Track it in My Reports.</p>
      <div className="sp-btn-row" style={{ marginTop: 24 }}>
        <button className="sp-btn sp-btn--ghost" onClick={() => { setDone(null); setForm({ name: "", walletAddress: "", sourceChain: "", txHash: "", issueType: "", message: "" }); setShot(null); setPreview(null); }}>
          Submit Another
        </button>
        <button className="sp-btn sp-btn--primary" onClick={onSubmitted}>View My Reports</button>
      </div>
    </div>
  );

  return (
    <div className="sp-new-form-wrap">
      <div className="sp-new-form-head">
        <h3 className="sp-section-title">Submit a Report</h3>
        <p className="sp-form-sub">Describe your issue and our team will get back to you via email.</p>
      </div>

      {err && <Err msg={err} onClose={() => setErr("")} />}

      <form onSubmit={submit} className="sp-form" noValidate>
        <div className="sp-form-row">
          <Field label="Your name" required>
            <input className="sp-input" type="text" placeholder="Name" value={form.name} onChange={set("name")} maxLength={100} required />
          </Field>
          <Field label="Issue type">
            <select className="sp-input sp-select" value={form.issueType} onChange={set("issueType")}>
              <option value="">— Select —</option>
              {ISSUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        <div className="sp-form-row">
          <Field label="Wallet address" hint="optional">
            <input className="sp-input sp-mono" type="text" placeholder="0x… / r… / Sol address" value={form.walletAddress} onChange={set("walletAddress")} maxLength={100} />
          </Field>
          <Field label="Source chain" hint="optional">
            <select className="sp-input sp-select" value={form.sourceChain} onChange={set("sourceChain")}>
              <option value="">— Select —</option>
              {SOURCE_CHAINS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Transaction hash" hint="optional">
          <input className="sp-input sp-mono" type="text" placeholder="0x…" value={form.txHash} onChange={set("txHash")} maxLength={200} />
        </Field>

        <Field label="Describe the issue" required>
          <textarea className="sp-input sp-textarea" rows={5} placeholder="What happened? Include token names, amounts, and any error messages you saw." value={form.message} onChange={set("message")} maxLength={2000} required />
          <span className="sp-char-count">{form.message.length} / 2000</span>
        </Field>

        <div className="sp-upload-wrap">
          <span className="sp-field-label">Screenshot <span className="sp-hint">optional, max 5 MB</span></span>
          {!preview ? (
            <label className="sp-dropzone">
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} hidden />
              <span className="sp-dropzone-icon">⬆</span>
              <span className="sp-dropzone-text">Click or drag an image here</span>
              <span className="sp-dropzone-hint">JPEG, PNG, GIF, or WEBP</span>
            </label>
          ) : (
            <div className="sp-preview-wrap">
              <img src={preview} alt="preview" className="sp-preview-img" />
              <button type="button" className="sp-preview-rm" onClick={removeFile}>Remove</button>
            </div>
          )}
        </div>

        <Btn busy={busy} label="Submit Report" />
      </form>
    </div>
  );
}

/* ─── shared primitives ───────────────────────────────────────────────────── */

function Field({ label, required, hint, children }) {
  return (
    <label className="sp-field">
      <span className="sp-field-label">
        {label}
        {required && <span className="sp-req"> *</span>}
        {hint    && <span className="sp-hint"> {hint}</span>}
      </span>
      {children}
    </label>
  );
}
function Btn({ busy, label }) {
  return <button className="sp-btn sp-btn--primary" type="submit" disabled={busy}>{busy ? "Please wait…" : label}</button>;
}
function Err({ msg, onClose }) {
  return (
    <div className="sp-err">
      <span>{msg}</span>
      <button type="button" onClick={onClose}>✕</button>
    </div>
  );
}

/* ─── styles ──────────────────────────────────────────────────────────────── */

function Styles() {
  return <style>{`
    /* ── page shell ── */
    .sp-page { min-height: 80vh; padding: 40px 16px 80px; }
    .sp-shell { max-width: 760px; margin: 0 auto; }

    /* ── header ── */
    .sp-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 0 0 28px; border-bottom: 1px solid rgba(255,214,102,.12); margin-bottom: 32px;
      flex-wrap: wrap; gap: 12px;
    }
    .sp-header-brand { display: flex; align-items: center; gap: 10px; }
    .sp-header-icon  { font-size: 22px; }
    .sp-header-title { font-size: 18px; font-weight: 700; color: #f0eadb; letter-spacing: -.01em; }
    .sp-header-user  { display: flex; align-items: center; gap: 12px; }
    .sp-header-email { font-size: 13px; color: #665e52; }

    /* ── auth panel ── */
    .sp-auth-wrap { display: flex; flex-direction: column; align-items: center; gap: 20px; }
    .sp-auth-card {
      width: 100%; max-width: 420px;
      background: rgba(12,16,28,.97); border: 1px solid rgba(255,214,102,.14);
      border-radius: 16px; padding: 36px 32px;
      box-shadow: 0 20px 60px rgba(0,0,0,.5);
    }
    .sp-auth-note { font-size: 12px; color: #504840; text-align: center; max-width: 340px; line-height: 1.6; }

    /* ── form primitives ── */
    .sp-form-head  { margin-bottom: 24px; }
    .sp-form-title { font-size: 22px; font-weight: 700; color: #f0eadb; margin: 0 0 6px; }
    .sp-form-sub   { font-size: 13px; color: #8a8070; margin: 0; line-height: 1.6; }
    .sp-form       { display: flex; flex-direction: column; gap: 16px; }
    .sp-form-footer{ display: flex; align-items: center; gap: 8px; justify-content: center; margin-top: 18px; flex-wrap: wrap; }
    .sp-form-row   { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
    @media (max-width: 520px) { .sp-form-row { grid-template-columns: 1fr; } }

    .sp-field        { display: flex; flex-direction: column; gap: 5px; }
    .sp-field-label  { font-size: 12px; font-weight: 600; color: #8a8070; text-transform: uppercase; letter-spacing: .06em; }
    .sp-req   { color: #e05c5c; }
    .sp-hint  { color: #504840; font-weight: 400; text-transform: none; letter-spacing: 0; font-size: 11px; }

    .sp-input {
      background: rgba(255,255,255,.04); border: 1px solid rgba(255,214,102,.13);
      border-radius: 9px; padding: 10px 13px; color: #f0eadb; font-size: 14px;
      outline: none; width: 100%; box-sizing: border-box; transition: border-color .15s;
    }
    .sp-input:focus { border-color: rgba(255,214,102,.45); }
    .sp-select  { appearance: none; cursor: pointer; }
    .sp-mono    { font-family: monospace; font-size: 13px; }
    .sp-textarea{ resize: vertical; min-height: 120px; font-family: inherit; }
    .sp-char-count { font-size: 11px; color: #504840; text-align: right; margin-top: -4px; }

    .sp-btn {
      border: none; border-radius: 10px; padding: 12px 22px;
      font-size: 14px; font-weight: 600; cursor: pointer; transition: opacity .15s;
    }
    .sp-btn--primary { background: linear-gradient(135deg,#d4a843,#f0c84a); color: #0a0e18; width: 100%; }
    .sp-btn--ghost   { background: rgba(255,255,255,.06); color: #c8bfb0; border: 1px solid rgba(255,214,102,.15); }
    .sp-btn--primary:disabled { opacity: .5; cursor: not-allowed; }
    .sp-btn-row      { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
    .sp-btn-row .sp-btn { width: auto; }

    .sp-link-btn { background: none; border: none; color: #d4a843; font-size: 13px; cursor: pointer; padding: 0; text-decoration: underline; text-underline-offset: 2px; }
    .sp-sep      { color: #504840; }

    .sp-err {
      display: flex; align-items: center; justify-content: space-between; gap: 10px;
      background: rgba(224,92,92,.1); border: 1px solid rgba(224,92,92,.25);
      color: #e05c5c; border-radius: 8px; padding: 10px 13px; font-size: 13px; margin-bottom: 4px;
    }
    .sp-err button { background: none; border: none; color: #e05c5c; cursor: pointer; font-size: 15px; }

    .sp-success-state { text-align: center; padding: 8px 0 4px; }
    .sp-success-icon  { font-size: 44px; margin-bottom: 12px; }

    /* ── upload ── */
    .sp-upload-wrap  { display: flex; flex-direction: column; gap: 7px; }
    .sp-dropzone {
      display: flex; flex-direction: column; align-items: center; gap: 5px;
      border: 1px dashed rgba(255,214,102,.22); border-radius: 10px; padding: 26px 16px;
      cursor: pointer; transition: border-color .15s;
    }
    .sp-dropzone:hover { border-color: rgba(255,214,102,.45); }
    .sp-dropzone-icon { font-size: 22px; color: #d4a843; }
    .sp-dropzone-text { font-size: 14px; color: #c8bfb0; }
    .sp-dropzone-hint { font-size: 12px; color: #504840; }
    .sp-preview-wrap  { position: relative; display: inline-block; }
    .sp-preview-img   { max-width: 100%; max-height: 200px; border-radius: 8px; border: 1px solid rgba(255,214,102,.15); }
    .sp-preview-rm    { position: absolute; top: 6px; right: 6px; background: rgba(224,92,92,.85); border: none; border-radius: 6px; color: #fff; padding: 3px 9px; font-size: 12px; cursor: pointer; }

    /* ── dashboard layout ── */
    .sp-dashboard { display: flex; flex-direction: column; gap: 0; }
    .sp-nav       {
      display: flex; gap: 6px; margin-bottom: 28px;
      border-bottom: 1px solid rgba(255,214,102,.1); padding-bottom: 0;
    }
    .sp-nav-item {
      display: flex; align-items: center; gap: 6px;
      background: none; border: none; border-bottom: 2px solid transparent;
      padding: 10px 16px; font-size: 14px; font-weight: 500; color: #665e52;
      cursor: pointer; transition: all .15s; margin-bottom: -1px;
    }
    .sp-nav-item:hover         { color: #c8bfb0; }
    .sp-nav-item--active       { color: #f0c84a; border-bottom-color: #f0c84a; }
    .sp-nav-icon               { font-size: 16px; }

    .sp-dashboard-body { min-height: 300px; }

    /* ── reports ── */
    .sp-reports         { display: flex; flex-direction: column; gap: 16px; }
    .sp-reports-topbar  { display: flex; align-items: center; justify-content: space-between; }
    .sp-reports-stats   { display: flex; align-items: center; gap: 0; }
    .sp-stat            { display: flex; flex-direction: column; align-items: center; padding: 0 18px 0 0; }
    .sp-stat-num        { font-size: 22px; font-weight: 700; color: #f0eadb; line-height: 1; }
    .sp-stat-label      { font-size: 11px; color: #665e52; margin-top: 3px; text-transform: uppercase; letter-spacing: .05em; }
    .sp-stat-divider    { width: 1px; height: 30px; background: rgba(255,214,102,.1); margin: 0 18px 0 0; }
    .sp-icon-btn        { background: none; border: 1px solid rgba(255,214,102,.15); color: #665e52; border-radius: 8px; padding: 6px 10px; font-size: 16px; cursor: pointer; transition: color .15s; }
    .sp-icon-btn:hover  { color: #d4a843; }

    .sp-ticket-list { display: flex; flex-direction: column; gap: 8px; }

    .sp-ticket {
      border: 1px solid rgba(255,214,102,.1); border-radius: 12px;
      overflow: hidden; transition: border-color .15s;
    }
    .sp-ticket--open     { }
    .sp-ticket--resolved { border-color: rgba(78,203,113,.15); }
    .sp-ticket-header {
      display: flex; align-items: center; justify-content: space-between;
      width: 100%; background: none; border: none;
      padding: 14px 18px; cursor: pointer; gap: 12px; flex-wrap: wrap;
    }
    .sp-ticket-header:hover { background: rgba(255,255,255,.02); }
    .sp-ticket-meta   { display: flex; align-items: center; gap: 10px; }
    .sp-ticket-type   { font-size: 14px; color: #c8bfb0; font-weight: 500; text-align: left; }
    .sp-ticket-aside  { display: flex; align-items: center; gap: 10px; }
    .sp-ticket-date   { font-size: 12px; color: #504840; }
    .sp-ticket-id     { font-size: 12px; color: #504840; font-family: monospace; }
    .sp-chevron       { font-size: 9px; color: #504840; }

    .sp-badge {
      font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 20px;
      white-space: nowrap; letter-spacing: .02em;
    }
    .sp-badge--amber { background: rgba(212,168,67,.15); color: #d4a843; }
    .sp-badge--green { background: rgba(78,203,113,.12); color: #4ecb71; }

    .sp-ticket-body {
      padding: 0 18px 16px; display: flex; flex-direction: column; gap: 14px;
      border-top: 1px solid rgba(255,214,102,.07);
    }
    .sp-ticket-message { display: flex; flex-direction: column; gap: 6px; padding-top: 14px; }
    .sp-field-text     { margin: 0; font-size: 14px; color: #c8bfb0; line-height: 1.7; white-space: pre-wrap; }
    .sp-ticket-fields  { display: flex; flex-direction: column; gap: 8px; }
    .sp-field-row      { display: flex; gap: 12px; align-items: baseline; flex-wrap: wrap; }
    .sp-field-val      { font-size: 13px; color: #c8bfb0; }
    .sp-hash           { word-break: break-all; }

    .sp-resolution {
      background: rgba(78,203,113,.07); border: 1px solid rgba(78,203,113,.18);
      border-radius: 10px; padding: 14px 16px;
    }
    .sp-resolution-head  { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .sp-resolution-dot   { width: 8px; height: 8px; border-radius: 50%; background: #4ecb71; flex-shrink: 0; }
    .sp-resolution-label { font-size: 12px; font-weight: 700; color: #4ecb71; text-transform: uppercase; letter-spacing: .06em; }
    .sp-resolution-date  { font-size: 11px; color: #504840; margin-left: auto; }
    .sp-resolution-text  { margin: 0; font-size: 14px; color: #c8bfb0; line-height: 1.7; white-space: pre-wrap; }

    .sp-pending {
      display: flex; align-items: center; gap: 8px;
      font-size: 13px; color: #8a8070;
    }
    .sp-pending-dot {
      width: 7px; height: 7px; border-radius: 50%; background: #d4a843; flex-shrink: 0;
      animation: sp-pulse 1.6s ease-in-out infinite;
    }
    @keyframes sp-pulse { 0%,100%{opacity:1} 50%{opacity:.25} }

    /* ── new report form ── */
    .sp-new-form-wrap { max-width: 620px; }
    .sp-new-form-head { margin-bottom: 24px; }
    .sp-section-title { font-size: 20px; font-weight: 700; color: #f0eadb; margin: 0 0 6px; }

    /* ── center states ── */
    .sp-center-state { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 56px 16px; gap: 8px; }
    .sp-state-title  { font-size: 18px; font-weight: 600; color: #c8bfb0; margin: 0; }
    .sp-state-msg    { font-size: 14px; color: #665e52; margin: 0; line-height: 1.6; max-width: 360px; }
    .sp-state-msg--error { color: #e05c5c; }

    .sp-empty-graphic { position: relative; width: 72px; height: 72px; margin-bottom: 8px; }
    .sp-empty-circle  { position: absolute; inset: 0; border-radius: 50%; background: rgba(255,214,102,.06); border: 1px solid rgba(255,214,102,.1); }
    .sp-empty-emoji   { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 30px; }

    .sp-spinner {
      width: 28px; height: 28px;
      border: 2px solid rgba(255,214,102,.12); border-top-color: #d4a843;
      border-radius: 50%; animation: sp-spin .7s linear infinite;
    }
    @keyframes sp-spin { to{transform:rotate(360deg)} }

    @media (max-width: 480px) {
      .sp-auth-card { padding: 28px 20px; }
      .sp-ticket-header { flex-direction: column; align-items: flex-start; }
    }
  `}</style>;
}
